import { useCallback, useEffect, useRef } from 'react';
import {
  normalizeLocale,
  readStoredLocalePreference,
  useAdminPreferredLocale,
  useAuthSession,
  useSetAdminPreferredLocale,
  useTranslation,
  writePersistedLocale,
  type SupportedLocale,
} from '@dukanti/shared';
import { supabase } from '@/lib/supabase';

function isEmailOnlyAdmin(user: ReturnType<typeof useAuthSession>['user']): boolean {
  return Boolean(user?.id && user.email && !user.phone);
}

export function AdminLocalePreferenceSync() {
  const { i18n } = useTranslation();
  const session = useAuthSession(supabase);
  const userId = session.user?.id ?? null;
  const canSync = !session.isLoading && isEmailOnlyAdmin(session.user);
  const currentLocale = normalizeLocale(i18n.language);
  const preferredLocale = useAdminPreferredLocale({
    client: supabase,
    userId,
    enabled: canSync,
  });
  const { mutate: setAdminPreferredLocale } = useSetAdminPreferredLocale({ client: supabase });
  const lastSyncedRef = useRef<string | null>(null);

  useEffect(() => {
    lastSyncedRef.current = null;
  }, [userId]);

  const syncDbLocale = useCallback(
    (locale: SupportedLocale) => {
      if (!canSync || !userId) return;
      const syncKey = `${userId}:${locale}`;
      if (lastSyncedRef.current === syncKey) return;
      lastSyncedRef.current = syncKey;
      setAdminPreferredLocale(
        { preferredLocale: locale },
        {
          onError: () => {
            lastSyncedRef.current = null;
          },
        },
      );
    },
    [canSync, setAdminPreferredLocale, userId],
  );

  useEffect(() => {
    if (!canSync || !userId || !preferredLocale.isSuccess) return;

    const storedLocale = readStoredLocalePreference();
    if (storedLocale) {
      syncDbLocale(storedLocale);
      return;
    }

    const dbLocale = preferredLocale.data;
    if (!dbLocale) return;

    lastSyncedRef.current = `${userId}:${dbLocale}`;
    writePersistedLocale(dbLocale);
    if (dbLocale !== currentLocale) {
      void i18n.changeLanguage(dbLocale);
    }
  }, [
    canSync,
    currentLocale,
    i18n,
    preferredLocale.data,
    preferredLocale.isSuccess,
    syncDbLocale,
    userId,
  ]);

  useEffect(() => {
    if (!canSync || !userId) return;
    const storedLocale = readStoredLocalePreference();
    if (!storedLocale || storedLocale !== currentLocale) return;
    syncDbLocale(currentLocale);
  }, [canSync, currentLocale, syncDbLocale, userId]);

  return null;
}
